import { useState, useEffect, useCallback } from 'react';
import { DashboardFilters, DashboardKPI, SalesData, RealTimeMetric, LocationSalesData } from '../types/dashboard.types';
import { apiClient } from '@/services/api.service';

export const useDashboardData = () => {
  const [filters, setFilters] = useState<DashboardFilters>({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    locations: ['all']
  });

  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState<DashboardKPI[]>([]);
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [realTimeMetrics, setRealTimeMetrics] = useState<RealTimeMetric[]>([]);
  const [locationSalesData, setLocationSalesData] = useState<LocationSalesData[]>([]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const locationId = 'all';
      const response = await apiClient.get(`/reports/sales/${locationId}`, {
        desde: filters.startDate,
        hasta: filters.endDate
      });

      if (response.success && response.data) {
        const { resumen, comparacion, ventasPorHora, ventasPorSucursal } = response.data;

        // KPIs
        const ventasChange = parseFloat(comparacion?.ingresosVariacion || '0');
        const ticketsChange = parseFloat(comparacion?.ventasVariacion || '0');
        const prevTicketPromedio = resumen.totalVentas > 0 && ticketsChange !== 0
          ? resumen.ticketPromedio / (1 + ticketsChange / 100)
          : resumen.ticketPromedio;
        const ticketPromedioChange = prevTicketPromedio > 0
          ? ((resumen.ticketPromedio - prevTicketPromedio) / prevTicketPromedio) * 100
          : 0;

        setKpis([
          {
            id: 'daily-sales',
            title: 'Ventas del Período',
            value: resumen.ingresoTotal,
            change: ventasChange,
            changeType: ventasChange >= 0 ? 'increase' : 'decrease',
            format: 'currency',
            icon: 'TrendingUp'
          },
          {
            id: 'tickets-issued',
            title: 'Cantidad de Tickets Emitidos',
            value: resumen.totalVentas,
            change: Math.abs(ticketsChange),
            changeType: ticketsChange >= 0 ? 'increase' : 'decrease',
            format: 'number',
            icon: 'Receipt'
          },
          {
            id: 'average-ticket',
            title: 'Ticket Promedio',
            value: resumen.ticketPromedio,
            change: parseFloat(Math.abs(ticketPromedioChange).toFixed(1)),
            changeType: ticketPromedioChange >= 0 ? 'increase' : 'decrease',
            format: 'currency',
            icon: 'Receipt'
          }
        ]);

        // Ventas por hora
        const hoursData: SalesData[] = (ventasPorHora || []).map((h: { hora: number; cantidad: number; ingresos: number }) => ({
          time: `${String(h.hora).padStart(2, '0')}:00`,
          sales: h.ingresos,
          orders: h.cantidad
        }));
        setSalesData(hoursData);

        // Ventas por sucursal
        if (ventasPorSucursal) {
          const locData: LocationSalesData[] = ventasPorSucursal.map((loc: {
            locationId: number;
            locationName: string;
            totales: { ventas: number; ingresos: number };
          }) => ({
            locationId: String(loc.locationId),
            locationName: loc.locationName,
            sales: loc.totales.ventas,
            revenue: loc.totales.ingresos,
            orders: loc.totales.ventas
          }));
          setLocationSalesData(locData);
        } else {
          setLocationSalesData([]);
        }

        setRealTimeMetrics([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // En caso de error, mostrar datos vacíos
      setKpis([
        { id: 'daily-sales', title: 'Ventas del Período', value: 0, change: 0, changeType: 'neutral', format: 'currency', icon: 'TrendingUp' },
        { id: 'tickets-issued', title: 'Cantidad de Tickets Emitidos', value: 0, change: 0, changeType: 'neutral', format: 'number', icon: 'Receipt' },
        { id: 'average-ticket', title: 'Ticket Promedio', value: 0, change: 0, changeType: 'neutral', format: 'currency', icon: 'Receipt' }
      ]);
      setSalesData([]);
      setLocationSalesData([]);
      setRealTimeMetrics([]);
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    filters,
    setFilters,
    loading,
    kpis,
    salesData,
    realTimeMetrics,
    locationSalesData
  };
};
